"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { Eye, EyeOff, Mail, Lock, ArrowLeft, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { signIn } from "@/lib/services/auth";
import type { SignInRequest } from "@/lib/services/auth";
import { resetPasswordAction } from "@/lib/actions/auth-actions";
import Loading from "@/components/ui/loading";
import { checkSession } from "@/lib/services/auth";

// 이메일 형식 검증 정규식
const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

/** 배경 + 헤더(로고/네비) 공용 셸 */
function LoginShell(t0) {
  const $ = _c(5);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Image data-imp-o="app/auth/login/page.tsx:25:7" src="/main-bg.png" alt="" fill={true} priority={true} sizes="100vw" className="object-cover" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Link data-imp-o="app/auth/login/page.tsx:36:9" href="/" aria-label={"\uD50C\uC5D0\uC774\uC2A4 \uC601\uC5B4 \uD648"}><Image data-imp-o="app/auth/login/page.tsx:37:11" src="/logo.png" alt={"\uD50C\uC5D0\uC774\uC2A4 \uC601\uC5B4"} width={150} height={46} priority={true} className="h-9 w-auto sm:h-10" /></Link>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <header data-imp="app/auth/login/page.tsx:35:7" className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-6 py-5 sm:px-10">{t2}<nav data-imp="app/auth/login/page.tsx:46:9" className="flex items-center gap-6 text-sm font-medium text-slate-700"><a data-imp="app/auth/login/page.tsx:47:11" href="https://placeenglish.co.kr/AI-admin" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-slate-900">서비스 소개</a><a data-imp="app/auth/login/page.tsx:56:11" href="tel:1551-9870" className="transition-colors hover:text-slate-900" title={"\uACE0\uAC1D\uC13C\uD130 1551-9870"}>고객센터</a></nav></header>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children) {
    t4 = <div data-imp="app/auth/login/page.tsx:23:5" className="relative min-h-screen w-full overflow-hidden bg-slate-100">{t1}{t3}{children}</div>;
    $[3] = children;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
function LoginForm() {
  const _router = useRouter();
  const searchParams = useSearchParams();
  const [formData, setFormData] = useState<SignInRequest>({
    email: "",
    password: ""
  });
  const [mode, setMode] = useState<"login" | "forgot">(searchParams.get("mode") === "forgot" ? "forgot" : "login");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  // Supabase 세션 체크 - 이미 로그인된 경우 홈으로 리다이렉트
  useEffect(() => {
    async function verifySession() {
      try {
        const hasSession = await checkSession();
        const redirectTo = searchParams.get("redirect");
        if (hasSession) {
          window.location.href = redirectTo || "/";
        } else {
          setCheckingSession(false);
        }
      } catch (error_0) {
        console.error("[Login] Session check failed:", error_0);
        setCheckingSession(false);
      }
    }
    verifySession();
  }, [searchParams]);
  useEffect(() => {
    const message = searchParams.get("message");
    if (message === "signup_success") {
      setSuccessMessage("회원가입이 완료되었습니다. 로그인해주세요.");
    }
  }, [searchParams]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e_0: React.FormEvent) => {
    e_0.preventDefault();
    setLoading(true);
    setError("");
    try {
      await signIn(formData);
      const redirectTo_0 = searchParams.get("redirect");
      // 로그인 성공 → 홈으로 리다이렉트 → UserStoreProvider가 자동 초기화
      window.location.href = redirectTo_0 || "/";
    } catch (error_1) {
      setError(error_1 instanceof Error ? error_1.message : "로그인에 실패했습니다.");
      setLoading(false);
    }
  };

  // 비밀번호 재설정 링크 발송
  const handleForgotSubmit = async (e_1: React.FormEvent) => {
    e_1.preventDefault();
    setError("");
    if (!EMAIL_REGEX.test(formData.email)) {
      setError("올바른 이메일 형식이 아닙니다.");
      return;
    }
    setLoading(true);
    try {
      const result = await resetPasswordAction(formData.email);
      if (result.success) {
        setResetSent(true);
      } else {
        setError(result.error || "이메일 발송에 실패했습니다.");
      }
    } catch (error_2) {
      setError(error_2 instanceof Error ? error_2.message : "이메일 발송에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 로그인 ↔ 비밀번호 찾기 전환
  const switchToForgot = () => {
    setError("");
    setSuccessMessage("");
    setResetSent(false);
    setMode("forgot");
  };
  const switchToLogin = () => {
    setError("");
    setResetSent(false);
    setMode("login");
  };

  // 세션 체크 중 로딩 표시
  if (checkingSession) {
    return <LoginShell data-imp-o="app/auth/login/page.tsx:185:7">
        <div data-imp="app/auth/login/page.tsx:186:9" className="flex min-h-screen items-center justify-center px-4">
          <div data-imp="app/auth/login/page.tsx:187:11" className="rounded-3xl border border-white/60 bg-white/80 p-8 backdrop-blur-md">
            <Loading data-imp-o="app/auth/login/page.tsx:188:13" />
            <p data-imp="app/auth/login/page.tsx:189:13" className="mt-4 text-center text-sm text-slate-600">
              로그인 상태 확인 중...
            </p>
          </div>
        </div>
      </LoginShell>;
  }
  const isForgot = mode === "forgot";
  const greeting = isForgot ? "비밀번호 찾기" : "안녕하세요 원장님!";
  const showSubmit = !(isForgot && resetSent);
  return <LoginShell data-imp-o="app/auth/login/page.tsx:203:5">
      <div data-imp="app/auth/login/page.tsx:204:7" className="relative z-10 flex min-h-screen items-center justify-center px-4 pt-24 pb-10 sm:px-8 lg:justify-between lg:px-16 lg:pt-0 xl:px-24">
        {/* 좌측 마케팅 카피 */}
        <div data-imp="app/auth/login/page.tsx:206:9" className="hidden max-w-md lg:block">
          <p data-imp="app/auth/login/page.tsx:207:11" className="text-base font-normal tracking-wide text-slate-800">
            PL:ace English 학원 관리 시스템
          </p>
          <h2 data-imp="app/auth/login/page.tsx:210:11" className="mt-4 text-4xl font-bold leading-snug xl:text-5xl">
            <span data-imp="app/auth/login/page.tsx:211:13" className="text-slate-800">AI 플키로 관리하고</span>
            <br data-imp="app/auth/login/page.tsx:212:13" />
            <span data-imp="app/auth/login/page.tsx:213:13" className="text-blue-700">원장님은</span>
            <br data-imp="app/auth/login/page.tsx:214:13" />
            <span data-imp="app/auth/login/page.tsx:215:13" className="text-blue-700">교육에 집중하세요</span>
          </h2>
          <p data-imp="app/auth/login/page.tsx:217:11" className="mt-6 text-lg font-semibold leading-relaxed text-slate-600 xl:text-xl">
            수업·숙제·출결·상담·리포트
            <br data-imp="app/auth/login/page.tsx:219:13" />
            학원운영을 자동화하는{" "}
            <span data-imp="app/auth/login/page.tsx:221:13" className="font-semibold text-blue-700">AI어드민</span>
          </p>
        </div>

        {/* 로그인 카드 클러스터 */}
        <div data-imp="app/auth/login/page.tsx:226:9" className="flex w-full max-w-md flex-col items-center">
          <form data-imp="app/auth/login/page.tsx:227:11" onSubmit={isForgot ? handleForgotSubmit : handleSubmit} className="relative w-full pt-14">
            {/* 아바타 (블루 바 위로 걸침) */}
            <div data-imp="app/auth/login/page.tsx:232:13" className="absolute left-1/2 top-0 z-20 -translate-x-1/2">
              <Image data-imp-o="app/auth/login/page.tsx:233:15" src="/plkey-icon.png" alt="AI 플키" width={80} height={80} priority className="h-20 w-20" />
            </div>

            {/* 블루 헤더 바 + 인사 문구 */}
            <div data-imp="app/auth/login/page.tsx:244:13" className="relative z-10 rounded-2xl bg-place-blue pb-2 pt-6 text-center backdrop-blur-sm">
              <h1 data-imp="app/auth/login/page.tsx:245:15" className="text-2xl font-bold text-white">{greeting}</h1>
            </div>

            {/* 화이트 바디 (블루 바보다 좁게 → 블루 바가 좌우로 overhang) */}
            <div data-imp="app/auth/login/page.tsx:249:13" className="-mt-4 mx-3 rounded-2xl bg-white/75 px-7 pb-16 pt-14 backdrop-blur-md">
              {/* 성공 메시지 */}
              {successMessage && <div data-imp="app/auth/login/page.tsx:252:17" className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                  {successMessage}
                </div>}

              {/* 에러 메시지 */}
              {error && <div data-imp="app/auth/login/page.tsx:259:17" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>}

              {/* 비밀번호 재설정 완료 */}
              {isForgot && resetSent ? <div data-imp="app/auth/login/page.tsx:266:17" className="text-center">
                  <div data-imp="app/auth/login/page.tsx:267:19" className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
                    <CheckCircle data-imp-o="app/auth/login/page.tsx:268:21" className="h-7 w-7 text-green-600" />
                  </div>
                  <h2 data-imp="app/auth/login/page.tsx:270:19" className="mb-2 text-lg font-semibold text-slate-800">
                    이메일을 확인해주세요
                  </h2>
                  <p data-imp="app/auth/login/page.tsx:273:19" className="text-sm leading-relaxed text-slate-600">
                    <span data-imp="app/auth/login/page.tsx:274:21" className="font-medium text-slate-800">
                      {formData.email}
                    </span>
                    <br data-imp="app/auth/login/page.tsx:277:21" />위 주소로 비밀번호 재설정 링크를 발송했습니다.
                  </p>
                  <button data-imp="app/auth/login/page.tsx:279:19" type="button" onClick={switchToLogin} className="mt-6 inline-flex items-center text-sm font-medium text-place-blue hover:underline">
                    <ArrowLeft data-imp-o="app/auth/login/page.tsx:284:21" className="mr-1 h-4 w-4" />
                    로그인으로 돌아가기
                  </button>
                </div> : isForgot ? <>
                  {/* 안내 문구 */}
                  <p data-imp="app/auth/login/page.tsx:291:19" className="mb-5 text-center text-sm leading-relaxed text-slate-500">
                    가입한 이메일 주소로
                    <br data-imp="app/auth/login/page.tsx:293:21" />
                    비밀번호 재설정 링크를 보내드립니다.
                  </p>

                  {/* 이메일 입력 */}
                  <div data-imp="app/auth/login/page.tsx:298:19" className="space-y-2">
                    <Label data-imp-o="app/auth/login/page.tsx:299:21" htmlFor="email" className="text-base text-slate-600">
                      이메일
                    </Label>
                    <div data-imp="app/auth/login/page.tsx:302:21" className="relative">
                      <Mail data-imp-o="app/auth/login/page.tsx:303:23" className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                      <Input data-imp-o="app/auth/login/page.tsx:304:23" id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="가입한 이메일을 입력하세요" className="h-12 rounded-2xl border-slate-200 bg-white/70 pl-12" required disabled={loading} />
                    </div>
                  </div>

                  {/* 로그인으로 돌아가기 */}
                  <div data-imp="app/auth/login/page.tsx:319:19" className="mt-5 text-center">
                    <button data-imp="app/auth/login/page.tsx:320:21" type="button" onClick={switchToLogin} className="inline-flex items-center text-sm font-medium text-place-blue hover:underline">
                      <ArrowLeft data-imp-o="app/auth/login/page.tsx:325:23" className="mr-1 h-4 w-4" />
                      로그인으로 돌아가기
                    </button>
                  </div>
                </> : <>
                  {/* 이메일 입력 */}
                  <div data-imp="app/auth/login/page.tsx:333:19" className="space-y-2">
                    <Label data-imp-o="app/auth/login/page.tsx:334:21" htmlFor="email" className="text-base text-slate-600">
                      이메일
                    </Label>
                    <div data-imp="app/auth/login/page.tsx:337:21" className="relative">
                      <Mail data-imp-o="app/auth/login/page.tsx:338:23" className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                      <Input data-imp-o="app/auth/login/page.tsx:339:23" id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="이메일을 입력하세요" className="h-12 rounded-2xl border-slate-200 bg-white/70 pl-12" required disabled={loading} />
                    </div>
                  </div>

                  {/* 비밀번호 입력 */}
                  <div data-imp="app/auth/login/page.tsx:354:19" className="mt-5 space-y-2">
                    <Label data-imp-o="app/auth/login/page.tsx:355:21" htmlFor="password" className="text-base text-slate-600">
                      비밀번호
                    </Label>
                    <div data-imp="app/auth/login/page.tsx:358:21" className="relative">
                      <Lock data-imp-o="app/auth/login/page.tsx:359:23" className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                      <Input data-imp-o="app/auth/login/page.tsx:360:23" id="password" name="password" type={showPassword ? "text" : "password"} value={formData.password} onChange={handleInputChange} placeholder="비밀번호를 입력하세요" className="h-12 rounded-2xl border-slate-200 bg-white/70 pl-12 pr-12" required disabled={loading} />
                      <button data-imp="app/auth/login/page.tsx:371:23" type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" disabled={loading}>
                        {showPassword ? <EyeOff data-imp-o="app/auth/login/page.tsx:378:27" className="h-5 w-5" /> : <Eye data-imp-o="app/auth/login/page.tsx:380:27" className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  {/* 비밀번호 찾기 전환 */}
                  <div data-imp="app/auth/login/page.tsx:387:19" className="mt-5 text-center">
                    <button data-imp="app/auth/login/page.tsx:388:21" type="button" onClick={switchToForgot} className="text-sm font-medium text-place-blue hover:underline">
                      비밀번호를 잊으셨나요?
                    </button>
                  </div>
                </>}
            </div>

            {/* 제출 버튼 (카드 하단에 걸침) */}
            {showSubmit && <Button data-imp-o="app/auth/login/page.tsx:402:15" type="submit" className="absolute bottom-0 left-1/2 z-20 h-14 w-56 -translate-x-1/2 translate-y-1/2 rounded-full bg-place-blue text-lg font-bold text-white shadow-lg shadow-place-blue/30 hover:bg-place-blue/90" disabled={loading}>
                {isForgot ? loading ? "발송 중..." : "재설정 링크 보내기" : loading ? "로그인 중..." : "로그인"}
              </Button>}
          </form>

          {/* 문의 뱃지 (카드 밖 하단) */}
          <div data-imp="app/auth/login/page.tsx:419:11" className="mt-12 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white/80 px-4 py-1.5 text-xs backdrop-blur-sm">
            <span data-imp="app/auth/login/page.tsx:420:13" className="font-semibold text-place-blue">문의</span>
            <span data-imp="app/auth/login/page.tsx:421:13" className="text-slate-600">support@placekey.kr</span>
          </div>
        </div>
      </div>
    </LoginShell>;
}
export default function LoginPage() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense data-imp-o="app/auth/login/page.tsx:431:5" fallback={<LoginShell data-imp-o="app/auth/login/page.tsx:433:9"><div data-imp="app/auth/login/page.tsx:434:11" className="flex min-h-screen items-center justify-center px-4"><div data-imp="app/auth/login/page.tsx:435:13" className="rounded-3xl border border-white/60 bg-white/80 p-8 backdrop-blur-md"><Loading data-imp-o="app/auth/login/page.tsx:436:15" /></div></div></LoginShell>}><LoginForm data-imp-o="app/auth/login/page.tsx:442:7" /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}